import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { http, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import LocationPicker from "../components/site/LocationPicker";

const Tab = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${active ? "bg-[var(--navy)] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={testId}>{children}</button>
);

function MobileInput({ value, onChange, testId }) {
  return (
    <div className="flex">
      <span className="h-12 px-3 flex items-center rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-slate-600">+91</span>
      <input className="field !rounded-l-none" required inputMode="numeric" maxLength={10} placeholder="मोबाईल नंबर" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} data-testid={testId} />
    </div>
  );
}

function OtpStep({ mobile, purpose, otp, setOtp }) {
  const [demo, setDemo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) return toast.error("10 अंकी मोबाईल नंबर टाका");
    setBusy(true);
    try { const { data } = await http.post("/auth/send-otp", { mobile, purpose }); setDemo(data.demo_otp); toast.success("OTP पाठवला"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input className="field" required inputMode="numeric" maxLength={6} placeholder="6 अंकी OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid={`${purpose}-otp-input`} />
        <button type="button" onClick={send} disabled={busy} className="h-12 px-4 shrink-0 rounded-lg border-2 border-[var(--orange)] text-[var(--navy)] font-semibold hover:bg-orange-50 disabled:opacity-60" data-testid={`${purpose}-send-otp-button`}>{busy ? "..." : demo ? "पुन्हा पाठवा" : "OTP पाठवा"}</button>
      </div>
      {demo && <p className="text-sm rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`${purpose}-demo-otp`}>Demo OTP (SMS लवकरच): <b className="tracking-widest">{demo}</b></p>}
    </div>
  );
}

function PinInput({ value, onChange, placeholder, testId }) {
  return (
    <input className="field tracking-[0.5em] text-center font-semibold" type="password" required inputMode="numeric" pattern="\d{4}" minLength={4} maxLength={4} title="4 अंकी PIN" autoComplete="off"
      placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid={testId} />
  );
}

function LoginForm({ onDone }) {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onDone((await http.post("/auth/login", { mobile, password })).data); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="member-login-form">
      <MobileInput value={mobile} onChange={setMobile} testId="login-mobile-input" />
      <PinInput value={password} onChange={setPassword} placeholder="4 अंकी PIN" testId="login-password-input" />
      <SubmitBtn busy={busy} testId="login-submit-button">Login करा</SubmitBtn>
      <p className="text-xs text-center text-slate-500">OTP फक्त पहिल्या नोंदणीवेळी. एकदा Login केल्यावर 1 वर्ष Login राहाल.</p>
    </form>
  );
}

function RegisterForm({ onDone }) {
  const [f, setF] = useState({ name: "", mobile: "", otp: "", password: "", district: "", taluka: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onDone((await http.post("/auth/register", f)).data, true); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="member-register-form">
      <input className="field" required minLength={2} placeholder="पूर्ण नाव · Full Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" />
      <MobileInput value={f.mobile} onChange={(mobile) => setF({ ...f, mobile })} testId="register-mobile-input" />
      <OtpStep mobile={f.mobile} purpose="register" otp={f.otp} setOtp={(otp) => setF({ ...f, otp })} />
      <PinInput value={f.password} onChange={(password) => setF({ ...f, password })} placeholder="4 अंकी PIN तयार करा" testId="register-password-input" />
      <LocationPicker required district={f.district} taluka={f.taluka} onChange={(district, taluka) => setF({ ...f, district, taluka })} testPrefix="register" />
      <SubmitBtn busy={busy} testId="register-submit-button">नोंदणी करा · Register</SubmitBtn>
    </form>
  );
}

function ResetForm({ onReset }) {
  const [f, setF] = useState({ mobile: "", otp: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await http.post("/auth/reset-password", f); toast.success("PIN बदलला. आता Login करा."); onReset(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="member-reset-form">
      <MobileInput value={f.mobile} onChange={(mobile) => setF({ ...f, mobile })} testId="reset-mobile-input" />
      <OtpStep mobile={f.mobile} purpose="reset" otp={f.otp} setOtp={(otp) => setF({ ...f, otp })} />
      <PinInput value={f.password} onChange={(password) => setF({ ...f, password })} placeholder="नवीन 4 अंकी PIN" testId="reset-password-input" />
      <SubmitBtn busy={busy} testId="reset-submit-button">PIN बदला</SubmitBtn>
    </form>
  );
}

const SubmitBtn = ({ busy, testId, children }) => (
  <button disabled={busy} className="w-full h-12 rounded-xl bg-[var(--sell)] hover:bg-[#c62828] text-white font-display font-bold text-lg disabled:opacity-60 flex items-center justify-center gap-2" data-testid={testId}>
    {busy && <Loader2 className="w-5 h-5 animate-spin" />}{children}
  </button>
);

export default function Login() {
  const [mode, setMode] = useState("login");
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const done = (data, isNew) => {
    signIn(data);
    toast.success(isNew ? "स्वागत आहे! 3 दिवसांचा Free Trial सुरू झाला." : `नमस्कार, ${data.user.name}`);
    nav(params.get("next") || "/account");
  };
  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
        <div className="flex items-center gap-2 text-[var(--navy)]"><KeyRound className="w-6 h-6 text-[var(--orange)]" /><h1 className="font-display font-extrabold text-2xl">{mode === "reset" ? "PIN विसरलात?" : "Login / नोंदणी"}</h1></div>
        {mode !== "reset" && (
          <div className="mt-5 flex gap-2 p-1 rounded-xl bg-slate-100">
            <Tab active={mode === "login"} onClick={() => setMode("login")} testId="tab-login">Login</Tab>
            <Tab active={mode === "register"} onClick={() => setMode("register")} testId="tab-register">नवीन नोंदणी</Tab>
          </div>
        )}
        <div className="mt-5">
          {mode === "login" && <LoginForm onDone={done} />}
          {mode === "register" && <RegisterForm onDone={done} />}
          {mode === "reset" && <ResetForm onReset={() => setMode("login")} />}
        </div>
        <div className="mt-4 text-sm text-center">
          {mode === "login" && <button onClick={() => setMode("reset")} className="text-[var(--buy)] hover:underline" data-testid="forgot-password-link">PIN विसरलात?</button>}
          {mode === "reset" && <button onClick={() => setMode("login")} className="text-[var(--buy)] hover:underline" data-testid="back-to-login-link">← Login</button>}
        </div>
      </div>
      <p className="mt-4 text-xs text-center text-slate-500">ब्राउझिंगसाठी Login ची गरज नाही. <Link to="/buy" className="text-[var(--buy)]">जाहिराती पहा</Link></p>
    </div>
  );
}
